import { Link } from "react-router-dom";
import { PageHeader } from "@/components/layout/PageHeader";
import { motion, useReducedMotion } from "framer-motion";
import { MessageSquare, Users, UserCheck, TrendingUp, Clock, Plus, Home, Zap } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useDashboard, useQuickActions } from "../hooks/useDashboard";
import {
  useRealTimeDashboard,
  useRealTimeNotifications,
} from "@/features/conversations/hooks/useWebSocket";
import { LiveStatsBar } from "./LiveStatsBar";
import { KpiCard } from "./KpiCard";
import { RecentActivityFeed } from "./RecentActivityFeed";
import { QuickActionsList } from "./QuickActionsList";
import { LeadsChart } from "./LeadsChart";
import { PlatformDistributionChart } from "./PlatformDistributionChart";
import { ConnectionStatusGrid } from "./ConnectionStatusGrid";
import { SetupChecklist } from "@/features/onboarding/SetupChecklist";
import { containerVariants, itemVariants } from "./motion";
import { mockOverview, mockLiveStats } from "./mockData";
import { USE_MOCKS } from "@/lib/mockConfig";
import type { DashboardOverview, LiveStats } from "@/types/api";

const EMPTY_OVERVIEW: DashboardOverview = {
  totalLeads: 0,
  activeLeads: 0,
  conversionRate: 0,
  responseTime: 0,
  connectedAccounts: { meta: 0, ecommerce: 0, total: 0 },
  recentActivity: [],
  leadsOverTime: [],
  platformDistribution: [],
};

const EMPTY_LIVE_STATS: LiveStats = {
  activeConnections: 0,
  messagesLastHour: 0,
  pendingLeads: 0,
  systemHealth: "healthy",
};

const DashboardSkeleton = () => (
  <div className="flex-1 p-4 md:p-6 bg-slate-50 dark:bg-slate-950 transition-colors">
    <div className="mx-auto max-w-7xl space-y-4 animate-pulse">
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <div className="h-7 w-40 rounded-xl bg-slate-200 dark:bg-slate-800" />
          <div className="h-4 w-80 rounded-xl bg-slate-200 dark:bg-slate-800" />
        </div>
        <div className="flex space-x-3">
          <div className="h-9 w-32 rounded-xl bg-slate-200 dark:bg-slate-800" />
          <div className="h-9 w-32 rounded-xl bg-slate-200 dark:bg-slate-800" />
        </div>
      </div>
      <div className="h-12 rounded-2xl bg-slate-200 dark:bg-slate-800" />
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4 md:gap-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="h-28 rounded-2xl bg-slate-200 dark:bg-slate-800" />
        ))}
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="h-72 rounded-2xl bg-slate-200 dark:bg-slate-800 lg:col-span-2" />
        <div className="h-72 rounded-2xl bg-slate-200 dark:bg-slate-800" />
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="h-64 rounded-2xl bg-slate-200 dark:bg-slate-800" />
        <div className="h-64 rounded-2xl bg-slate-200 dark:bg-slate-800" />
      </div>
    </div>
  </div>
);

export function DashboardContent() {
  const { overview, liveStats, isLoading, error } = useDashboard();
  const { quickActions } = useQuickActions();
  const { liveMetrics } = useRealTimeDashboard();
  const prefersReducedMotion = useReducedMotion();

  // Enable real-time notifications
  useRealTimeNotifications();

  if (isLoading) {
    return <DashboardSkeleton />;
  }

  // ⚠️ Demo data only when mocks are switched on (never in a production build). A real tenant
  // whose request failed, or who has no data yet, must see zeros and the error note below, not
  // invented leads and customers.
  const data = overview || (USE_MOCKS ? mockOverview : EMPTY_OVERVIEW);
  const fallbackStats: LiveStats = USE_MOCKS
    ? mockLiveStats
    : { ...EMPTY_LIVE_STATS, systemHealth: error ? "warning" : "healthy" };
  const stats = liveStats || liveMetrics || fallbackStats;

  return (
    <div className="flex-1 bg-transparent p-4 md:p-6">
      <motion.div
        className="mx-auto max-w-7xl space-y-4 md:space-y-5"
        variants={containerVariants}
        initial={prefersReducedMotion ? false : "hidden"}
        animate="visible"
      >
        {/* First-run checklist: renders nothing once every step is done or it is hidden. */}
        <SetupChecklist />

        {error && !USE_MOCKS && (
          <div
            role="status"
            className="rounded-lg border border-amber-200 dark:border-amber-800/60 bg-amber-50 dark:bg-amber-950/40 px-4 py-3 text-sm text-amber-900 dark:text-amber-200"
          >
            Some dashboard figures could not be loaded, so they show as zero for now. They refresh
            automatically.
          </div>
        )}

        {/* Live Stats Bar */}
        <motion.div variants={itemVariants}>
          <LiveStatsBar stats={stats} />
        </motion.div>

        {/* KPI Row: ServerlessLens Grouped Telemetry Pods */}
        <motion.div
          className="grid grid-cols-1 gap-3.5 lg:grid-cols-2"
          variants={containerVariants}
        >
          {/* Pod 1: Pipeline & Capture Telemetry */}
          <motion.div variants={itemVariants}>
            <Card className="max-w-none overflow-hidden rounded-2xl border border-slate-200/90 dark:border-slate-800 bg-slate-50/40 dark:bg-slate-900/40 shadow-xs">
              <div className="flex items-center justify-between border-b border-slate-200/70 dark:border-slate-800 bg-slate-100/50 dark:bg-slate-850/60 px-3.5 py-2">
                <div className="flex items-center gap-2">
                  <span className="flex h-5 w-5 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <Users className="h-3 w-3" />
                  </span>
                  <span className="text-[11px] font-bold tracking-wider uppercase text-slate-800 dark:text-slate-200">
                    Lead Pipeline
                  </span>
                  <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200/80 dark:border-emerald-800/60 bg-emerald-50 dark:bg-emerald-950/40 px-2 py-0.2 text-[9px] font-bold text-emerald-700 dark:text-emerald-300">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                    Live Feed
                  </span>
                </div>
                <span className="text-[10px] font-medium text-slate-400 dark:text-slate-500">All channels active</span>
              </div>

              <div className="grid grid-cols-2 gap-2.5 p-2.5 sm:p-3 bg-white/70 dark:bg-slate-900/70">
                <KpiCard
                  title="Total Leads"
                  value={data.totalLeads || 0}
                  trend={USE_MOCKS ? 12 : undefined}
                  icon={<Users className="h-4 w-4" />}
                  tone="primary"
                  to="/leads"
                  linkTitle="View all leads"
                  description="Total identified prospective customers captured across Facebook, Instagram, WhatsApp, and your Shopify storefront."
                />
                <KpiCard
                  title="Active Leads"
                  value={data.activeLeads || 0}
                  trend={USE_MOCKS ? 5 : undefined}
                  icon={<UserCheck className="h-4 w-4" />}
                  tone="success"
                  to="/leads"
                  linkTitle="View active leads"
                  description="Leads with ongoing active conversations or recent automated interactions within the last 30 days."
                />
              </div>
            </Card>
          </motion.div>

          {/* Pod 2: Conversion & Automation Telemetry */}
          <motion.div variants={itemVariants}>
            <Card className="max-w-none overflow-hidden rounded-2xl border border-slate-200/90 dark:border-slate-800 bg-slate-50/40 dark:bg-slate-900/40 shadow-xs">
              <div className="flex items-center justify-between border-b border-slate-200/70 dark:border-slate-800 bg-slate-100/50 dark:bg-slate-850/60 px-3.5 py-2">
                <div className="flex items-center gap-2">
                  <span className="flex h-5 w-5 items-center justify-center rounded-lg bg-indigo-500/10 dark:bg-indigo-500/20 text-indigo-600 dark:text-indigo-400">
                    <TrendingUp className="h-3 w-3" />
                  </span>
                  <span className="text-[11px] font-bold tracking-wider uppercase text-slate-800 dark:text-slate-200">
                    Performance &amp; Speed
                  </span>
                  <span className="inline-flex items-center gap-1 rounded-full border border-indigo-200/80 dark:border-indigo-800/60 bg-indigo-50 dark:bg-indigo-950/40 px-2 py-0.2 text-[9px] font-bold text-indigo-700 dark:text-indigo-300">
                    Measured
                  </span>
                </div>
                <span className="text-[10px] font-medium text-slate-400 dark:text-slate-500">Real-time sync</span>
              </div>

              <div className="grid grid-cols-2 gap-2.5 p-2.5 sm:p-3 bg-white/70 dark:bg-slate-900/70">
                <KpiCard
                  title="Conversion Rate"
                  value={data.conversionRate || 0}
                  suffix="%"
                  decimals={1}
                  trend={USE_MOCKS ? -2 : undefined}
                  icon={<TrendingUp className="h-4 w-4" />}
                  tone="accent"
                  to="/analytics"
                  linkTitle="View analytics"
                  description="Percentage of customer inquiries and leads that resulted in completed purchases or orders."
                />
                <KpiCard
                  title="Avg Response"
                  value={data.responseTime || 0}
                  suffix="m"
                  decimals={1}
                  trend={USE_MOCKS ? -15 : undefined}
                  icon={<Clock className="h-4 w-4" />}
                  tone="warning"
                  to="/analytics"
                  linkTitle="View response metrics"
                  description="Average response time taken by AI automation and your team to reply to inbound customer messages."
                />
              </div>
            </Card>
          </motion.div>
        </motion.div>

        {/* Activity + Quick Actions: ServerlessLens Pods */}
        <motion.div className="grid grid-cols-1 gap-3.5 lg:grid-cols-3" variants={containerVariants}>
          <motion.div className="lg:col-span-2" variants={itemVariants}>
            <Card className="max-w-none h-full overflow-hidden rounded-2xl border border-slate-200/90 dark:border-slate-800 bg-slate-50/40 dark:bg-slate-900/40 shadow-xs flex flex-col">
              <div className="flex items-center justify-between border-b border-slate-200/70 dark:border-slate-800 bg-slate-100/50 dark:bg-slate-850/60 px-3.5 py-2">
                <div className="flex items-center gap-2">
                  <span className="flex h-5 w-5 items-center justify-center rounded-lg bg-blue-500/10 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400">
                    <MessageSquare className="h-3 w-3" />
                  </span>
                  <span className="text-[11px] font-bold tracking-wider uppercase text-slate-800 dark:text-slate-200">
                    Recent Activity
                  </span>
                  <span className="inline-flex items-center gap-1 rounded-full border border-blue-200/80 dark:border-blue-800/60 bg-blue-50 dark:bg-blue-950/40 px-2 py-0.2 text-[9px] font-bold text-blue-700 dark:text-blue-300">
                    Live Stream
                  </span>
                </div>
                <Link
                  to="/conversations"
                  className="text-[10px] font-semibold text-primary hover:text-primary/80 transition-colors flex items-center gap-0.5"
                >
                  View All
                  <span aria-hidden="true">→</span>
                </Link>
              </div>
              <div className="flex-1 p-3 bg-white/70 dark:bg-slate-900/70">
                <RecentActivityFeed activities={data.recentActivity || []} />
              </div>
            </Card>
          </motion.div>

          <motion.div variants={itemVariants}>
            <Card className="max-w-none h-full overflow-hidden rounded-2xl border border-slate-200/90 dark:border-slate-800 bg-slate-50/40 dark:bg-slate-900/40 shadow-xs flex flex-col">
              <div className="flex items-center justify-between border-b border-slate-200/70 dark:border-slate-800 bg-slate-100/50 dark:bg-slate-850/60 px-3.5 py-2">
                <div className="flex items-center gap-2">
                  <span className="flex h-5 w-5 items-center justify-center rounded-lg bg-amber-500/10 dark:bg-amber-500/20 text-amber-600 dark:text-amber-400">
                    <Zap className="h-3 w-3" />
                  </span>
                  <span className="text-[11px] font-bold tracking-wider uppercase text-slate-800 dark:text-slate-200">
                    Quick Actions
                  </span>
                </div>
                <span className="text-[10px] font-medium text-slate-400 dark:text-slate-500">Shortcuts</span>
              </div>
              <div className="flex-1 p-2.5 bg-white/70 dark:bg-slate-900/70">
                <QuickActionsList actions={quickActions || []} />
              </div>
            </Card>
          </motion.div>
        </motion.div>

        {/* Charts: ServerlessLens Pods */}
        <motion.div className="grid grid-cols-1 gap-3.5 lg:grid-cols-2" variants={containerVariants}>
          <motion.div variants={itemVariants}>
            <Card className="max-w-none overflow-hidden rounded-2xl border border-slate-200/90 dark:border-slate-800 bg-slate-50/40 dark:bg-slate-900/40 shadow-xs">
              <div className="flex items-center justify-between border-b border-slate-200/70 dark:border-slate-800 bg-slate-100/50 dark:bg-slate-850/60 px-3.5 py-2">
                <div className="flex items-center gap-2">
                  <span className="flex h-5 w-5 items-center justify-center rounded-lg bg-emerald-500/10 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400">
                    <TrendingUp className="h-3 w-3" />
                  </span>
                  <span className="text-[11px] font-bold tracking-wider uppercase text-slate-800 dark:text-slate-200">
                    Leads Over Time
                  </span>
                  <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200/80 dark:border-emerald-800/60 bg-emerald-50 dark:bg-emerald-950/40 px-2 py-0.2 text-[9px] font-bold text-emerald-700 dark:text-emerald-300">
                    Timeline
                  </span>
                </div>
                <span className="text-[10px] font-medium text-slate-400 dark:text-slate-500">30-day window</span>
              </div>
              <div className="p-3 bg-white/70 dark:bg-slate-900/70">
                <LeadsChart data={data.leadsOverTime || []} />
              </div>
            </Card>
          </motion.div>

          <motion.div variants={itemVariants}>
            <Card className="max-w-none overflow-hidden rounded-2xl border border-slate-200/90 dark:border-slate-800 bg-slate-50/40 dark:bg-slate-900/40 shadow-xs">
              <div className="flex items-center justify-between border-b border-slate-200/70 dark:border-slate-800 bg-slate-100/50 dark:bg-slate-850/60 px-3.5 py-2">
                <div className="flex items-center gap-2">
                  <span className="flex h-5 w-5 items-center justify-center rounded-lg bg-indigo-500/10 dark:bg-indigo-500/20 text-indigo-600 dark:text-indigo-400">
                    <Users className="h-3 w-3" />
                  </span>
                  <span className="text-[11px] font-bold tracking-wider uppercase text-slate-800 dark:text-slate-200">
                    Platform Distribution
                  </span>
                  <span className="inline-flex items-center gap-1 rounded-full border border-indigo-200/80 dark:border-indigo-800/60 bg-indigo-50 dark:bg-indigo-950/40 px-2 py-0.2 text-[9px] font-bold text-indigo-700 dark:text-indigo-300">
                    Share
                  </span>
                </div>
                <span className="text-[10px] font-medium text-slate-400 dark:text-slate-500">Active channels</span>
              </div>
              <div className="p-3 bg-white/70 dark:bg-slate-900/70">
                <PlatformDistributionChart data={data.platformDistribution || []} />
              </div>
            </Card>
          </motion.div>
        </motion.div>

        {/* Connection Status */}
        <ConnectionStatusGrid />
      </motion.div>
    </div>
  );
}
